import { CollectionNode, CollectionNodeObject, CollectionNodeSequences, SequencedItem } from "types";

/**
 * Constructs the full path for a collection item based on parent collection's path and ID.
 * Path format: grandParentId/parentId/childId
 *
 * @example
 * getConstructedItemPath("", "parent1") // Returns: "parent1"
 * getConstructedItemPath("grandparent/parent", "child") // Returns: "grandparent/parent/child"
 */
export const getConstructedItemPath = (parentCollectionPath: string, parentCollectionId: ID): string => {
    // If parent path is empty, just use the parent ID
    if (!parentCollectionPath) {
        return parentCollectionId;
    }
    // Otherwise, append parent ID to the path: grandParentId/parentId
    return `${parentCollectionPath}/${parentCollectionId}`;
};

/**
 * Calculates the nesting depth of a collection based on its path.
 * Depth is determined by counting slash-delimited IDs in the path.
 */
export const getCollectionNestingDepth = (path: string): number => {
    if (!path || path === "") {
        return 0;
    }
    return path.split("/").length;
};

/**
 * Extracts the parent ID from a collection's path.
 * Path format: "grandparent/parent" - the last ID is the parent.
 * Returns 0 if path is empty (indicating root level).
 */
export const getParentIdFromPath = (path: string): string | number => {
    if (!path || path.trim() === "") {
        return 0; // Root level
    }
    const pathParts = path.split("/");
    return pathParts[pathParts.length - 1];
};

/**
 * Calculates the new sequence for a collection item being dropped at a specific position.
 * Uses midpoint between siblings or adds increment step.
 *
 * @param destinationIndex - The index where the item is being dropped
 * @param sortedSiblings - Array of sibling collection items sorted by sequence
 * @returns The new sequence number for the dropped item
 */
export const getCollectionDropSequence = (
    destinationIndex: number,
    sortedSiblings: Array<{ sequence: number }>,
): number => {
    const startSequence = CollectionNodeSequences.COLLECTION_NODE_SEQUENCE_START;
    const incrementStep = CollectionNodeSequences.COLLECTION_NODE_SEQUENCE_INCREMENT;

    if (destinationIndex === 0) {
        // Moving to first position
        return sortedSiblings.length === 0 || !sortedSiblings[0]
            ? startSequence
            : Math.floor((startSequence + sortedSiblings[0].sequence) / 2);
    }

    if (destinationIndex >= sortedSiblings.length) {
        // Moving to last position or beyond
        const lastSibling = sortedSiblings[sortedSiblings.length - 1];
        return lastSibling ? lastSibling.sequence + incrementStep : startSequence;
    }

    // Moving between two items
    const previousSibling = sortedSiblings[destinationIndex - 1];
    const nextSibling = sortedSiblings[destinationIndex];

    if (previousSibling && nextSibling) {
        // Calculate midpoint between previous and next
        return Math.floor((previousSibling.sequence + nextSibling.sequence) / 2);
    }

    if (previousSibling) {
        // Only previous exists, increment
        return previousSibling.sequence + incrementStep;
    }

    // Fallback
    return startSequence;
};

/**
 * Calculates the resulting sequence when reordering collection items via drag and drop.
 * Uses midpoint algorithm to place item between siblings.
 *
 * @param destinationIndex - The index where the item is being dropped
 * @param sourceIndex - The index from where the item is being dragged
 * @param items - Array of items with sequences (sorted by current display order)
 * @returns The new sequence number for the dragged item
 */
export const getCollectionItemResultingSequence = (
    destinationIndex: number,
    sourceIndex: number,
    items: SequencedItem[],
): number => {
    const startSequence = CollectionNodeSequences.COLLECTION_NODE_SEQUENCE_START;
    const incrementStep = CollectionNodeSequences.COLLECTION_NODE_SEQUENCE_INCREMENT;

    if (destinationIndex < sourceIndex) {
        // Moving item up (towards start)
        const previousItem = items[destinationIndex - 1];
        const currentItem = items[destinationIndex];

        if (previousItem && currentItem) {
            return Math.floor((previousItem.sequence + currentItem.sequence) / 2);
        }

        if (!previousItem && currentItem) {
            return Math.floor((startSequence + currentItem.sequence) / 2);
        }
    } else {
        // Moving item down (towards end)
        const nextItem = items[destinationIndex + 1];
        const currentItem = items[destinationIndex];

        if (nextItem && currentItem) {
            return Math.floor((nextItem.sequence + currentItem.sequence) / 2);
        }

        if (!nextItem && currentItem) {
            return currentItem.sequence + incrementStep;
        }
    }

    // Fallback
    return startSequence + incrementStep;
};

// Type guard to check if a COLLECTION NODE is a product/catalog collection item with required fields
export const isProductCollectionNode = (
    collectionNode: CollectionNode,
): collectionNode is CollectionNode & { id: ID; product: { id: ID } } => {
    return (
        (collectionNode.object === CollectionNodeObject.PRODUCT_COLLECTION_NODE ||
            collectionNode.object === CollectionNodeObject.CATALOG_ITEM_COLLECTION_NODE) &&
        "product" in collectionNode &&
        collectionNode.product?.id !== undefined &&
        "id" in collectionNode
    );
};
